import React, { useCallback, useMemo, useRef, useState } from 'react';
import shortid from 'shortid';
import AddIcon from '@mui/icons-material/Add';
import Form from '@/common/components/Form/index';
import DataEntryForm from '@/common/containers/FormConstructor/components/DataEntryForm';
import { useTranslation } from 'react-i18next';
import { useLocalizedField } from '@/common/hooks/index';
import { Button } from '@mui/material';
import styles from '@/common/containers/Forms/FinInstitutionsForm.styles';

const initialValue = (question, answers) => {
  if (!answers.length) {
    return [{ ...question, id: shortid.generate() }];
  }

  return answers.map((answer) => ({
    ...question,
    id: shortid.generate(),
    answerId: answer.id,
  }));
};

const getInitialValues = (questions, answersObject) =>
  questions.reduce((acc, { answerId, id }) => {
    const answer = answersObject[answerId];
    if (answer && answer.status === 'active') {
      acc[id] = {
        value: answer.value,
        answerId,
      };
    }
    return acc;
  }, {});

const FinInstitutionsForm = ({ question, answers = [], onSubmit, formId }) => {
  const { t } = useTranslation();
  const getLocalizedField = useLocalizedField();
  const answersObject = useMemo(
    () =>
      answers.reduce((acc, ans) => {
        acc[ans.id] = ans;
        return acc;
      }, {}),
    [answers]
  );
  const initialQuestionsRef = useRef(null);

  if (!initialQuestionsRef.current) {
    initialQuestionsRef.current = initialValue(question, answers);
  }

  const [questions, setQuestions] = useState(initialQuestionsRef.current);
  const initialValuesRef = useRef(null);

  if (!initialValuesRef.current) {
    initialValuesRef.current = getInitialValues(
      initialQuestionsRef.current,
      answersObject
    );
  }

  const addEntry = () => {
    setQuestions([...questions, { ...question, id: shortid.generate() }]);
  };

  const removeEntry = (questionId) => {
    setQuestions(questions.filter(({ id }) => id !== questionId));
  };

  const initialValues = initialValuesRef.current;

  const handleSubmit = useCallback(
    (form) => {
      const toDelete = { ...initialValues };

      const payload = [];
      Object.entries(form).forEach(([uid, { value }]) => {
        const initialValue = initialValues[uid];

        if (initialValue) delete toDelete[uid];

        payload.push({
          value,
          action: initialValue ? 'update' : 'create',
          answerId: initialValue ? initialValue.answerId : null,
        });
      });

      Object.values(toDelete).forEach(({ answerId }) => {
        payload.push({
          value: '',
          action: 'delete',
          answerId: answerId,
        });
      });

      return onSubmit(payload);
    },
    [initialValues, onSubmit]
  );

  return (
    <>
      <Form initialValues={initialValues} onSubmit={handleSubmit} id={formId}>
        {questions.map((question, ind) => {
          const onDelete = () => removeEntry(question.id);
          return (
            <DataEntryForm
              {...question}
              number={ind + 1}
              key={question.id}
              parentKey={question.id}
              canDelete={questions.length}
              onDelete={onDelete}
            />
          );
        })}
      </Form>
      <Button
        size="medium"
        color="success"
        startIcon={<AddIcon />}
        onClick={addEntry}
        sx={[
          styles.addButton,
          questions.length ? styles.addButtonWithEntries : null,
        ]}
      >
        {t('Add {{item}}', {
          item: getLocalizedField(question, 'listItemName'),
        })}
      </Button>
    </>
  );
};

export default FinInstitutionsForm;
